import {
  Input,
  OnInit,
  OnDestroy, 
  ChangeDetectorRef, 
  Directive,
  Injectable,
  Output,
  EventEmitter,
  inject,
  SimpleChanges,
  OnChanges,
  ViewChild
} from "@angular/core";
import {
  AsyncValidatorFn,
  ControlContainer,
  ControlValueAccessor,
  FormControl,
  FormControlStatus,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
} from "@angular/forms";
import { BaseFpxDataService, CriteriaQuery } from "../models/index";
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  finalize,
  map,
  Observable,
  of,
  startWith,
  Subject,
  Subscription,
  switchMap,
  takeUntil,
  tap,
} from "rxjs";
import { LookUpFn } from "../models/http-provider.model"; 
import { FpxFormAnchorDirective } from '../directives/error-handler/error-container.directive';
import { FPxFakeFormControlErrorMessageService } from "../services/form-control-error/form-control-error.service";
import { ViewApplicationStatusHelper } from "src/app/rcob/view-application-status/view-application-status.helper";

@Directive()
export abstract class BaseFpxControlComponent
  implements OnInit, OnDestroy, ControlValueAccessor
{
  /** @description Input Output variables */
  @Input() changePublisher!: Subject<{
    subject: string;
    payload: any;
  }>;
  @Input() formControlName!: string;
  @Input() checkOthers: string = "";
  @Input() patchFormcontrol: string = "";
  @Input() label: string = "";
  @Input() placeholder: string = "";
  @Input() dependencyField: string[] = [];
  @Input() controlName: string = "";
  @Input() selectableList$: Observable<Array<any>> = of([]);
  @Input() ngTemplateName!: string;
  @Output() commonControlEvent : EventEmitter<{eventName : string , payload : any}> = new EventEmitter<{eventName : string , payload : any}> ()
  
  @Input() selectableListService: BaseFpxDataService<any> | undefined;
  @Input() hint: string = "";
  @Input() controlValue: any;

  protected domainControlValue: any;
  private _registeredDependencies: string[] = [];
  private _dropdownMode: "BASIC" | "SEARCHABLE" = "BASIC";
  protected readonly changeDetectorRef: ChangeDetectorRef;
  pageOptions = {
    pageOffset: '1',
    pageCriteria: new CriteriaQuery(),
    totalRowCount: 0,
  };
  previousDropdownData: Array<any> = [];

  /** @description Template variables */
  selectableDataList$!: Observable<any>;
  formControl!: FormControl;
  loading: boolean = false;
  errorCode: string = "";
  private readonly input$ = new Subject<any>();

  inputChange$ = this.input$.asObservable();
  @Output() searchableDropdowninputChange: EventEmitter<{
    value: any;
    criteriaDetail: CriteriaQuery;
    checkId: boolean;
  }> = new EventEmitter<{ value: any; criteriaDetail: CriteriaQuery; checkId: boolean;}>();

  /** @description Private Variables */
  private readonly _syncValidators: ValidatorFn[] = [];
  private readonly _asyncValidators: AsyncValidatorFn[] = [];
  private readonly notifyContext: Map<string, any> = new Map<string, any>();
  private readonly _controlContainer: ControlContainer;
  private changePublisher$!: Subscription;
  private controlStatus$!: Subscription;
  public _dataService!: BaseFpxDataService<unknown>;
  private _helper!: BaseFpxControlHelper;
  // public viewApplicationStatusHelper:ViewApplicationStatusHelper= inject(ViewApplicationStatusHelper);
  public readonly errorMessageService : FPxFakeFormControlErrorMessageService = inject(FPxFakeFormControlErrorMessageService);
  public readonly formAnchorDirective : FpxFormAnchorDirective = inject(FpxFormAnchorDirective);
  private readonly destroy$: Subject<any> = new Subject<any>();

  /** to trigger the data load event  */
  public lookupResponse$: Subject<any> = new Subject<any>();
  /** initial formcontrol value check */
  initialFormControlValue: boolean = true;
  @ViewChild('fpxControl') fpxControl: BaseFpxControlComponent | undefined;
  /** core control events */
  coreControlEvent$: Subject<{ action: any; value: any }> =
    new Subject<{ action: any; value: any }>();

  /** core control type */
  coreControlType:
    | 'SEARCHABLEDROPDOWN'
    | 'SELECTABLEDROPDOWN'
    | 'TEXTBOX'
    | 'TEXTAREA'
    | 'RADIOBUTTON'
    | '' = '';
  constructor(
    controlContainer: ControlContainer,
    changeDetectorRef: ChangeDetectorRef,
    
  ) {
    this._controlContainer = controlContainer;
    this.changeDetectorRef = changeDetectorRef;
  }
  protected setHelper(helper: BaseFpxControlHelper) {
    this._helper = helper;
  }

  /** @description protected members */
  protected addSyncValidatorFn(syncValidatorFn: ValidatorFn) {
    this._syncValidators.push(syncValidatorFn);
  }

  protected addAsyncValidatorFn(asyncValidatorFn: AsyncValidatorFn) {
    this._asyncValidators.push(asyncValidatorFn);
  }

  protected setSelectableData(selectableDataList: LookUpFn<any>) {
    this.selectableDataList$ = selectableDataList().pipe(
      map((res) => {
        this.pageOptions.totalRowCount = res.totalRowCount || 0;
        if (this.pageOptions.pageOffset == '1') {
          if (
            res.data &&
            res.data.length > 0 &&
            ['SELECTABLEDROPDOWN'].includes(this.coreControlType)
          ) {
            res.data = [{ id: '', text: 'Please Select' }, ...res.data];
          } else if (
            res &&
            res.length > 0 &&
            ['SELECTABLEDROPDOWN'].includes(this.coreControlType)
          ) {
            res = [{ id: '', text: 'Please Select' }, ...res];
          }
          return [...(res.data || res)];
        } else {
          return [...this.previousDropdownData, ...(res.data || res)];
        }
      }),
      catchError((err: any) => {
        return of([]);
      }),
      tap((res: any) => {
        this._helper?.doLookUpResponse(res);
        if (this.fpxControl) {
          // this.fpxControl.lookupTrigger$.next(res);
        } else {
          this.lookupResponse$.next(res);
        }
        this.previousDropdownData = res;
      })
    );
    this.changeDetectorRef.detectChanges();
  }

  protected addDependencies(dependencyList: string[]) {
    this._registeredDependencies = dependencyList;
  }

  protected setDataSource(service: BaseFpxDataService<unknown>): void {
    this._dataService = service;
  }
  protected setDropdownMode(mode: "BASIC" | "SEARCHABLE"): void {
    this._dropdownMode = mode;
  }

  /** @description init lifecycle hook */
  ngOnInit(): void {
    if (this.controlName) {
      this.formControlName = this.controlName;
    }
    let formGroup = this._controlContainer.control as FormGroup;
    this.formControl = formGroup.controls[this.formControlName] as FormControl;
    this.doPreInit();
    this.formControl.addValidators(this._syncValidators);
    this.formControl.addAsyncValidators(this._asyncValidators);
    this.formControl.updateValueAndValidity();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes["controlValue"]?.currentValue !==
      changes["controlValue"]?.previousValue
    ) {
      this.customWriteValue(changes["controlValue"].currentValue);
    }
  }
  ngAfterViewInit() { 
    this._watchCoreControl();
    
    if (this.selectableListService) {
      this.setDataSource(this.selectableListService);
    }
    if (this.changePublisher) {
      this.changePublisher$ = this.changePublisher
        .asObservable()
        .subscribe((response) => {
          this.notifyContext.set(response.subject, response.payload);
          if (this.dependencyField.includes(response.subject)) {
            let dependentFieldValue: any = {};
            for (let index = 0; index < this.dependencyField.length; index++) {
              dependentFieldValue[this._registeredDependencies[index]] =
                this.notifyContext.has(this.dependencyField[index])
                  ? this.notifyContext.get(this.dependencyField[index])
                  : null;
            }
            this.doChangeNotify(response.subject, dependentFieldValue);
            this._helper?.doChangeNotify(response.subject, dependentFieldValue);
            this.changeDetectorRef.markForCheck();
            if (
              this._dropdownMode === "BASIC" &&
              this.dependencyField.length > 0
            ) {
              this._refereshDataBasic(dependentFieldValue);
            } else if (
              this._dropdownMode === "SEARCHABLE" &&
              this.dependencyField.length > 0
            ) {
              this._refereshDataSearchable(dependentFieldValue);
            } else if (
              this._dropdownMode === 'SEARCHABLE' &&
              this.dependencyField.length > 0 &&
              !this.objectNullCheck(dependentFieldValue)
            ) {
              this.setSelectableData(() => of([]));
            }
          }
        });
    }

    this.controlStatus$ = this.formControl.statusChanges.subscribe(
      (res: FormControlStatus) => {
        if (res === "INVALID" || this.formControl.errors) {
          this.errorCode = this.getErrorCode();
          console.log("error identified", this.formControlName, this.errorCode);
          this.changeDetectorRef.markForCheck();
        } else {
          this.errorCode = "";
        }
        if (res === "PENDING") {
          this.loading = true;
        } else {
          this.loading = false;
        }
      }
    );
    if (this._dropdownMode === "BASIC" && this.dependencyField.length === 0) {
      this._refereshDataBasic(null);
    } else if (
      this._dropdownMode === "SEARCHABLE" &&
      this.dependencyField.length === 0
    ) {
      this._refereshDataSearchable(null);
    }

    if (this._dropdownMode === "SEARCHABLE") {
      console.log("subscribing to value changes");
      this.formControl.valueChanges.subscribe((value: any) => {
        console.log("Change in value", value, typeof value);
        if (typeof value === "string" && value !== "") {
          const selectData$ =
            this._helper &&
            this._helper?.doPostLookupInterceptor(
              this._dataService.lookup({ searchText: value })(),
              { searchText: value }
            )
              ? () =>
                  this._helper?.doPostLookupInterceptor(
                    this._dataService.lookup({ searchText: value })(),
                    { searchText: value }
                  )
              : this._dataService?.lookup({ searchText: value });
          this.setSelectableData(selectData$);
        } else {
          this.onChange(value);
        }
        // console.log('companyValue =>',this.viewApplicationStatusHelper.companyValue)
        // if(this.viewApplicationStatusHelper.companyValue!==''){
        //    const selectData$ =
        //     this._helper &&
        //     this._helper?.doPostLookupInterceptor(
        //       this._dataService.lookup({ searchText: this.viewApplicationStatusHelper.companyValue })(),
        //       { searchText: this.viewApplicationStatusHelper.companyValue }
        //     )
        //       ? () =>
        //           this._helper?.doPostLookupInterceptor(
        //             this._dataService.lookup({ searchText: this.viewApplicationStatusHelper.companyValue })(),
        //             { searchText: this.viewApplicationStatusHelper.companyValue }
        //           )
        //       : this._dataService?.lookup({ searchText: this.viewApplicationStatusHelper.companyValue });
        //   this.setSelectableData(selectData$);
          
        // }
        // else{
        //   this.onChange(value);
        // }
      });
    } else {
      this.formControl.valueChanges.subscribe((value: any) => {
        this.changeDetectorRef.markForCheck();
      });
    }
    this._helper?.setControl(this.formControl);
    this._helper?.doPreInit();
    this.doPostInit();
    this._helper?.doPostInit();
  }

  private _watchCoreControl() {
    if (this.fpxControl) {
      this.fpxControl.coreControlEvent$
        .asObservable()
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: ({
            action,
            value,
          }: {
            action: any;
            value: any;
          }) => {
            this._coreControlAction[action]?.(value);
          },
          error: () => {},
          complete: () => {},
        });
      this.coreControlType = this.fpxControl.coreControlType;
    }
  }
  private _onValueSelect = (value: any) => {
    this.onChange(value);
    this.onTouched();
  };

  private _onDropdownChange = (value: any) => {
    this.commonControlEvent.next({
      eventName: `${this.formControlName}_onDropdownChange`,
      payload: value,
    });
  };
  private _coreControlAction: { [K in any]: any } = {
    SETVALUE: this._onValueSelect,
    ONDROPDOWNCHANGE: this._onDropdownChange,
  };
  detectChanges() {
    this.changeDetectorRef.detectChanges();
  }
  objectNullCheck(dependentFieldValue: { [k: string]: string }): boolean {
    let noNullValue = true;
    for (const key in dependentFieldValue) {
      if (
        Object.prototype.hasOwnProperty.call(dependentFieldValue, key) &&
        !dependentFieldValue[key]
      ) {
        noNullValue = false;
      }
    }
    return noNullValue;
  }
  

  private _refereshDataBasic(key: { [k: string]: any } | null) {
    try {
      if (this._dataService && this._dataService.lookup) {
        let keys: { [k: string]: any } = {};
        if (key) {
          for (let i = 0; i < this._registeredDependencies.length; i++) {
            keys[this._registeredDependencies[i]] =
              key[this._registeredDependencies[i]];
          }
        }
        const selectData$ =
        this._helper &&
        this._helper?.doPostLookupInterceptor(
          this._dataService.lookup(keys)(),
          key
        )
          ? () =>
              this._helper?.doPostLookupInterceptor(
                this._dataService.lookup(keys)(),
                key
              )
          : this._dataService.lookup(keys);
      this.setSelectableData(selectData$);
      this.changeDetectorRef.markForCheck();
      }
    } catch (error) {
      console.error("Data Service not implemented");
    }
  }

  private _refereshDataSearchable(key: { [k: string]: any } | null) {
    try {
      if (this._dataService) {
        const selectData$ =
        this._helper &&
        this._helper?.doPostLookupInterceptor(
          this._loadDynamicDropdown()(),
          key
        )
          ? () =>
              this._helper?.doPostLookupInterceptor(
                this._loadDynamicDropdown()(),
                key
              )
          : this._loadDynamicDropdown();
      this.setSelectableData(selectData$);
      this.changeDetectorRef.markForCheck();
      }
    } catch (error) {
      console.error("Data Service not implemented");
    }
  }

  private _loadDynamicDropdown(key: { [k: string]: string } = {}) {
    return () => {
      return this.inputChange$.pipe(
        startWith(this.formControl.value),
        debounceTime(1000),
        switchMap((value) => {
          this.loading = true;
          let criteriaQuery =
            this._helper?.doLookupCriteriaInterceptor(
              this.pageOptions.pageCriteria
            ) || this.pageOptions.pageCriteria;
          criteriaQuery.addFilterCritertia('text', 'String', 'startsWith', {
            searchText: value,
          });
          if (this.initialFormControlValue) {
            criteriaQuery.addFilterCritertia('id', 'String', 'equals', {
              searchText: value,
            });
            // criteriaQuery.setFilterExpression('or');
            criteriaQuery.removeFilterCriteria('text');
            this.initialFormControlValue = false;
          }
          let keys = { ...key, searchText: value };
          return this._dataService?.lookup(keys, new Map())()
            .pipe(
              map((item) => {
                if (
                  item &&
                  item.length === 0 &&
                  this.pageOptions.pageOffset === '1'
                ) {
                  return [{ id: -1, text: 'No Data Found' }];
                }
                return item;
              }),
              finalize(() => {
                this.loading = false;
                /** loading spinner need to handle here */
              })
            );
        })
      );
    };
    /*this.inputChange$.pipe(
      distinctUntilChanged(),
      startWith(''),
      debounceTime(200),
      switchMap((value) => {
        this.loading = true;
        return this._dataService
          .lookup(criteria)()
          .pipe(
            finalize(() => {
              this.loading = false;
            })
          );
      })
    );*/
  }

  /** @description destroy lifecycle hook */
  ngOnDestroy(): void {
    if (this.changePublisher$) {
      this.changePublisher$.unsubscribe();
    }
    if (this.controlStatus$) {
      this.controlStatus$.unsubscribe();
    }
    this.notifyContext.clear();
    this.doDestroy();
  }

  /** @description return the error code */
  private getErrorCode() {
    const errors = this.formControl.errors;
    if (errors == null || typeof errors !== 'object') {
      return '';
    }
    const keys = Object.keys(errors as ValidationErrors);
    if (!keys.length) {
      return '';
    }
    const validator = keys[0];
    let message = '';
    if (this.formAnchorDirective && this.formAnchorDirective.formName) {
      message =  this.errorMessageService.getErroMessage()?.[
        this.formAnchorDirective.formName
      ][`${this.formControlName}_${validator}`];
    } else{
      message = this.errorMessageService?.getErroMessage()?.["common"]?.[validator];
    }
     return message;
  }

  /** @description control vaule accessor */
  onChange: any = () => {};

  onTouched: () => void = () => {};

  setDisabledState?(isDisabled: boolean): void {
    console.error("isdisable", this.formControlName, isDisabled);
  }

  registerOnChange(fn: any): void {
    console.log(this.formControlName, "registerchange");
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  writeValue(obj: any): void {
    // if (this.formControl && obj != this.formControl.value) {
    //   this.formControl.setValue(obj, { emitEvent: false });
    // }
    if (this.formControl) {
      this.domainControlValue = obj;
      this.changeDetectorRef.markForCheck();
    }
    if (this.fpxControl) {
      this.fpxControl.customWriteValue(obj);
    }
  }

  public refreshState(): void {
    this.doRefresh();
  }

  public onInput(value: any) {
    // this.input$.next(value);
	  this.pageOptions.pageOffset = '1';
    this.searchableDropdowninputChange.emit({
      value: value,
      criteriaDetail: new CriteriaQuery(),
      checkId: false
    });
  }

  protected onItemSelect(value: { id: string; text: string }): void {
    this.onChange(value);
    this.onTouched();
  }

  protected itemDisplay(value: { id: string; text: string }): string {
    return value && value.text ? value.text : "";
  }

  /** @description override methods to access the angular life cycle hooks */
  protected doPreInit(): void {}
  protected doPostInit(): void {}
  protected doChangeNotify(subject: string, payload: any): void {}
  protected doDestroy(): void {}
  protected doRefresh(): void {}
  protected customWriteValue(payload: any): void {}
  protected customIsDisable(isDisabled: boolean): void {}
  protected setInitialSelectedData(list: any): void {}
  /**to handle the searchable dropdown */
  searchableDropdownChange(data: {
    value: any;
    criteriaDetail: CriteriaQuery;
  }): any {
    this.pageOptions.pageCriteria = data.criteriaDetail;
    // this.pageOptions.pageOffset = data.criteriaDetail.getPageOffset();
    this.input$.next(data.value);
  }
}
@Injectable()
export abstract class BaseFpxControlHelper {
  protected formControl!: FormControl;
  constructor() {}

  public setControl(control: FormControl) {
    this.formControl = control;
  }

  public doPostLookupInterceptor(
    data: Observable<any>,
    key: any
  ): Observable<any> {
    return data;
  }

  public doChangeNotify(subject: string, payload: any): void {}

  public doPreInit(): void {}

  public doPostInit() : void {}
  /**  */
  public doLookUpResponse(res: any) {}
public doLookupCriteriaInterceptor(value: any): CriteriaQuery {
    return new CriteriaQuery();
  }
}
